import { useEffect, useSyncExternalStore } from 'react';
import { action, subscribe } from './api.ts';

/**
 * Guests' orders and calls nobody has answered yet, for the number on the "Online" button.
 * One live connection for the whole app, however many screens show the number; a short
 * chime when something new arrives, so a busy waiter hears it.
 */

export interface Pending {
  requests: number;
  calls: number;
  /** Guests can use the menu or order online somewhere: the screens show the "Online" button. */
  enabled: boolean;
}

let pending: Pending = { requests: 0, calls: 0, enabled: false };
let location: string | null = null;
const listeners = new Set<() => void>();
let users = 0;
let stop: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
/** The first count after opening the app is not news: no chime for it. */
let loaded = false;

async function refresh() {
  try {
    const next = await action<Pending>('online.pending', { location });
    const more = next.requests > pending.requests || next.calls > pending.calls;
    if (next.requests !== pending.requests || next.calls !== pending.calls || next.enabled !== pending.enabled) {
      pending = next;
      for (const l of listeners) l();
    }
    if (more && loaded) chime();
    loaded = true;
  } catch {
    // Offline or signed out: the connection bar says so already.
  }
}

/** Two soft notes. Browsers allow sound only after the person has touched the page once. */
function chime() {
  try {
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      const at = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.4);
    });
    setTimeout(() => void ctx.close(), 1200);
  } catch {
    // No sound on this device.
  }
}

/** The count for this device's location (all locations when it has none), kept live while shown. */
export function useOnlinePending(enabled: boolean, here: string | null): Pending {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => pending,
  );
  useEffect(() => {
    if (!enabled) return;
    if (location !== here) {
      location = here;
      void refresh();
    }
    users++;
    if (!stop) {
      void refresh();
      stop = subscribe(['online', 'restaurant.table'], (event) => {
        if (event.startsWith('online.') || event === 'restaurant.table.changed') void refresh();
      });
      // A missed event (a dropped connection) is caught up within a minute.
      timer = setInterval(() => void refresh(), 60_000);
    }
    return () => {
      users--;
      if (users === 0) {
        stop?.();
        stop = null;
        if (timer) clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled, here]);
  return value;
}

/** Ask the counter to look again now (after accepting or answering something). */
export const refreshPending = () => void refresh();
